import { memo } from 'react';
import { toaster } from '@optiaxiom/react';
import { IconLink } from '@optimizely/axiom-icons';
import { NodeProps } from '@xyflow/react';

import {
  createWebhookTrigger,
  deleteWebhookTrigger,
  getWebhookTrigger,
  updateWebhookTrigger,
} from '../../services/webhook-trigger';
import { NodeConnectionTypes, NodeTypeCategory } from '../../types/connections';
import { NODE_TYPE } from '../../types/node';
import { defineNode } from '../../utils/node-factory';
import { BaseNodeRenderer } from '../components/renderers/BaseNodeRenderer/BaseNodeRenderer';

/**
 * Configuration for the Webhook Trigger node type.
 * Triggers workflow execution via HTTP webhook.
 */
export const webhookTriggerConfig = defineNode({
  description: {
    displayName: 'Webhook Trigger',
    name: NODE_TYPE.WEBHOOK,
    description: 'Trigger workflow execution via HTTP webhook',
    iconInfo: {
      icon: IconLink,
      color: 'fg.accent',
    },
    category: NodeTypeCategory.TRIGGER,
    input: {
      type: NodeConnectionTypes.Main,
      filter: undefined,
    },
    output: {
      type: NodeConnectionTypes.Main,
      displayName: 'Output',
      maxConnections: 1,
    },
    properties: [
      {
        displayName: 'Name',
        name: 'name',
        type: 'string',
        required: true,
        default: '',
        description: 'A descriptive name for this webhook trigger',
      },
      {
        displayName: 'Description',
        name: 'description',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional description of when this webhook should be called',
      },
      {
        displayName: 'Payload Content Type',
        name: 'payload_content_type',
        type: 'string',
        required: true,
        default: 'application/json',
        description: 'Content type for webhook payload',
      },
      {
        displayName: 'Payload Schema',
        name: 'payload_schema',
        type: 'string',
        typeOptions: {
          rows: 2,
        },
        required: false,
        default: '',
        description: 'JSON schema defining the expected webhook payload structure',
      },
      {
        displayName: 'Auth Key',
        name: 'auth_key',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional authentication key for webhook security',
      },
      {
        displayName: 'Auth Header Name',
        name: 'auth_header_name',
        type: 'string',
        required: false,
        default: '',
        description: 'Header name for authentication (e.g., "Authorization")',
      },
      {
        displayName: 'Auth Header Format',
        name: 'auth_header_format',
        type: 'string',
        required: false,
        default: '',
        description: 'Format for auth header value. Use {token} as placeholder',
      },
      {
        displayName: 'Webhook URL',
        name: 'webhook_url',
        type: 'string',
        required: false,
        default: '',
        readonly: true,
        description:
          'URL to call this webhook trigger. Copy this URL to integrate with external systems.',
      },
    ],
    defaults: {
      name: 'Webhook Trigger',
      parameters: {
        name: '',
        description: '',
        payload_content_type: 'application/json',
        payload_schema: '',
        auth_key: '',
        auth_header_name: '',
        auth_header_format: '',
        webhook_url: '',
      },
    },
  },

  enableAsyncGet: (_nodeId: string, nodeData?: Record<string, unknown>) => {
    const links =
      nodeData && typeof nodeData === 'object' ? (nodeData['links'] as Record<string, string>) : {};
    return Boolean(links && links['self'] && links['self'].trim().length > 0);
  },

  onAsyncSave: async (
    nodeId: string,
    data: Record<string, unknown>,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    console.warn('context.nodeData', context.nodeData);
    if (!context.nodeData || !('links' in context.nodeData)) {
      const webhookTrigger = await createWebhookTrigger(
        context.workflowAgentId,
        context.instanceId,
        data
      );
      return webhookTrigger;
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      await updateWebhookTrigger(url, data);
    } catch (error) {
      toaster.create('Failed to update webhook trigger', { intent: 'danger' });
      throw error;
    }
  },

  onAsyncGet: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    if (!context.nodeData || !('links' in context.nodeData)) {
      throw new Error('Node data not found in the context');
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      const webhookTrigger = await getWebhookTrigger(url);
      return webhookTrigger;
    } catch (error) {
      if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        return {};
      }

      console.error('Failed to fetch webhook trigger:', error);
      toaster.create('Failed to load webhook trigger configuration', { intent: 'danger' });
      throw error;
    }
  },

  onAsyncDelete: async (
    _nodeId: string,
    context?: { instanceId?: string; workflowAgentId?: string; nodeData?: Record<string, unknown> }
  ) => {
    if (!context?.instanceId || !context?.workflowAgentId) {
      throw new Error('Instance ID or Workflow Agent ID not provided in context');
    }

    if (!context.nodeData || !('links' in context.nodeData)) {
      throw new Error('Node data not found in the context');
    }

    const url = (context.nodeData['links'] as Record<string, string>)?.self;
    if (!url) {
      throw new Error('URL not found in node data');
    }

    try {
      await deleteWebhookTrigger(url);
      toaster.create('Webhook trigger deleted successfully', { intent: 'success' });
    } catch (error) {
      console.error('Failed to delete webhook trigger:', error);
      toaster.create('Failed to delete webhook trigger', { intent: 'danger' });
      throw error;
    }
  },

  render: memo(function WebhookTriggerRenderer(props: NodeProps) {
    return <BaseNodeRenderer {...props} />;
  }),
});
